<template>
  <div
    :class="[
      `HRStepCard-${documentName}`,
      { active: active, 'is-disabled': disabled },
    ]"
    class="HRStepCard"
    :data-test-id="`hr-step-card-${documentName}`"
    @click="click"
  >
    <div class="HRStepCard__icon-container">
      <div class="HRStepCard__icon">
        <slot name="icon"></slot>
      </div>
    </div>
    <div class="HRStepCard__body">
      <div class="HRStepCard__body__title">{{ title }}</div>
    </div>
    <div class="HRStepCard__completion-icon-container">
      <span v-if="optional" class="HRStepCard__completion-icon__optional">
        optional
      </span>
      <co-icon
        v-if="state.hasBackendError"
        class="HRStepCard__warning-icon"
        data-test-id="warning-icon"
        name="exclamation-triangle"
      />
      <template v-else>
        <co-icon
          v-if="!optional && completion >= 100"
          class="HRStepCard__completion-icon-complete"
          name="check"
        ></co-icon>
        <hr-pie-icon
          v-if="!optional && completion > 0 && completion < 100"
          :stage="state.stage"
        />
      </template>
    </div>
  </div>
</template>

<script>
import HrPieIcon from '@/components/hr-pie-icon.vue';
import { computed, reactive, watch } from 'vue';
import { CoIcon } from '@deputy/copilot2';
import store from '@/store';

export default {
  name: 'hr-step-card',
  components: { CoIcon, HrPieIcon },
  props: {
    title: {
      type: String,
      default: null,
    },
    active: {
      type: Boolean,
      default: false,
    },
    optional: {
      type: Boolean,
      default: false,
    },
    completion: {
      type: Number,
      default: null,
    },
    documentName: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  setup(props, { emit }) {
    const state = reactive({
      stage: null,
      hasBackendError: computed(() => {
        if (store.state.onboarding.countryCode === 'US') {
          return (
            store.state.indexedDocumentErrors[props.documentName]?.errors
              .length ?? false
          );
        }
        return store.state.documentErrors?.some((error) =>
          error.includes(props.documentName)
        );
      }),
    });

    function click() {
      emit('click');
    }

    watch(
      () => props.completion,
      () => {
        state.stage = 0;

        if (props.completion >= 100) {
          state.stage = 4;
          return;
        }
        if (props.completion >= 75) {
          state.stage = 3;
          return;
        }
        if (props.completion >= 25) {
          state.stage = 2;
          return;
        }
        if (props.completion > 0) {
          state.stage = 1;
        }
      },
      {
        immediate: true,
      }
    );
    return {
      state,
      click,
    };
  },
};
</script>

<style scoped lang="scss">
@import '~@/core/style-vars.scss';

.HRStepCard {
  border-radius: 6px;
  border: 1px solid var(--co-lines-border-solid);
  padding: 20px;
  display: flex;
  align-items: center;
  cursor: pointer;
  height: 90px;
  margin-bottom: 15px;
}

.HRStepCard__icon {
  font-size: 20px;
  display: flex;
  justify-content: center;
}

.HRStepCard__icon-container {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  justify-content: center;
  display: flex;
  flex-direction: column;
}

.HRStepCard {
  color: var(--co-text-primary);
  border-color: var(--co-lines-border-transparent);
}

.HRStepCard.active {
  border-color: var(--co-accent-secondary);
  background-color: var(--co-accent-background-secondary);
}
.HRStepCard.is-disabled {
  color: var(--co-text-secondary);
  cursor: not-allowed;
}
.HRStepCard.is-disabled.HRStepCard:nth-child(n) .HRStepCard__icon-container {
  background-color: var(--co-background-secondary);
  border-color: var(--co-lines-border-transparent);
}
.HRStepCard.is-disabled:nth-child(n) .HRStepCard__icon {
  color: var(--co-icon-subtle);
}

.HRStepCard .HRStepCard__icon {
  color: var(--co-text-primary);
}

.HRStepCard-personalDetails .HRStepCard__icon {
  color: var(--blue-1100);
}
.HRStepCard-personalDetails .HRStepCard__icon-container {
  background: var(--blue-300);
  border: 1px solid var(--blue-400);
}

.HRStepCard-bankDetails .HRStepCard__icon {
  color: var(--pink-1100);
}
.HRStepCard-bankDetails .HRStepCard__icon-container {
  border: 1px solid var(--pink-400);
  background-color: var(--pink-300);
}

.HRStepCard-employmentEligibilityVerification .HRStepCard__icon,
.HRStepCard-tfnDeclaration .HRStepCard__icon {
  color: var(--yellow-1100);
}
.HRStepCard-employmentEligibilityVerification .HRStepCard__icon-container,
.HRStepCard-tfnDeclaration .HRStepCard__icon-container,
.HRStepCard-taxDetails .HRStepCard__icon-container {
  border: 1px solid var(--yellow-400);
  background: var(--yellow-300);
}

.HRStepCard-superannuation .HRStepCard__icon {
  color: var(--orange-1100);
}
.HRStepCard-superannuation .HRStepCard__icon-container {
  border: 1px solid var(--orange-400);
  background: var(--orange-300);
}

.HRStepCard-workRights .HRStepCard__icon {
  color: var(--lime-1100);
}
.HRStepCard-workRights .HRStepCard__icon-container {
  border: 1px solid var(--lime-400);
  background: var(--lime-300);
}

.HRStepCard-taxDetails .HRStepCard__icon,
.HRStepCard-federalTaxForm .HRStepCard__icon,
.HRStepCard-superAPI .HRStepCard__icon {
  color: var(--teal-1100);
}

.HRStepCard-taxDetails .HRStepCard__icon-container,
.HRStepCard-federalTaxForm .HRStepCard__icon-container,
.HRStepCard-superAPI .HRStepCard__icon-container {
  border: 1px solid var(--teal-400);
  background: var(--teal-300);
}

.HRStepCard-stateTaxForm .HRStepCard__icon {
  color: var(--purple-1100);
}
.HRStepCard-stateTaxForm .HRStepCard__icon-container {
  border: 1px solid var(--purple-400);
  background: var(--purple-300);
}

.HRStepCard-additionalQuestions .HRStepCard__icon {
  color: var(--violet-1100);
}
.HRStepCard-additionalQuestions .HRStepCard__icon-container {
  border: 1px solid var(--violet-400);
  background: var(--violet-300);
}

.HRStepCard-fileUploads .HRStepCard__icon {
  color: var(--tomato-1100);
}
.HRStepCard-fileUploads .HRStepCard__icon-container {
  background-color: var(--tomato-300);
  border: 1px solid var(--tomato-400);
}

.HRStepCard-docSignatureRequests .HRStepCard__icon {
  color: var(--ochre-1100);
}
.HRStepCard-docSignatureRequests .HRStepCard__icon-container {
  background-color: var(--ochre-300);
  border: 1px solid var(--ochre-400);
}

.HRStepCard__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: $step_card_body-padding;
}

.HRStepCard__body__title {
  font-size: 18px;
  font-weight: $font-weight-bold;
}

.HRStepCard__completion-icon-container {
  width: 50px;
  justify-content: center;
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
  color: var(--green-1100);
}

.HRStepCard.active .hr-pie-icon {
  border-color: var(--co-accent-primary);
}

.HRStepCard__body__completion {
  font-size: 14px;
  line-height: 14px;
  text-transform: capitalize;
}

.HRStepCard__completion-icon-complete {
  width: 20px !important;
  height: 20px !important;
}

.HRStepCard__completion-icon__stage {
  width: 18px;
  height: 18px;
  display: none;
}

.HRStepCard__completion-icon__stage1 {
  display: inline;
  position: absolute;
  top: -10px;
  right: -10px;
}

.HRStepCard__completion-icon__stage2 {
  display: inline;
  position: absolute;
  top: 0;
  right: -10px;
}

.HRStepCard__completion-icon__stage3 {
  display: inline;
  position: absolute;
  bottom: -10px;
  right: 0;
}

.HRStepCard__completion-icon__stage.show {
  display: inline;
}

.HRStepCard__warning-icon {
  width: 20px !important;
  height: 20px !important;
  color: var(--co-status-red);
}
</style>
